/* eslint-disable perfectionist/sort-objects */

// Define configuration variables to be used in the React app
// -- no secrets here --
type BuildEnv =
  'development' | 'local' | 'preview' | 'production' | 'sandbox' | 'staging';
type NonProdBuildEnv = Exclude<BuildEnv, 'production' | 'staging'>;
export const environmentName = (process.env.BUILD_ENV || 'sandbox') as BuildEnv;

// Internal/non-customer environments. Listed by inclusion so an unknown
// BUILD_ENV counts as customer-facing.
export const NON_PROD_ENVIRONMENTS = [
  'local',
  'development',
  'preview',
  'sandbox',
] as const satisfies readonly NonProdBuildEnv[];

export const packageVersion = process.env.PACKAGE_VERSION;

// Default environment variables for BUILD_ENV=development
//
// Auth provider default (Auth0 vs Cognito): flip `COGNITO_ENABLED` per
// environment in the switch below to cut an env over to Cognito -- commit the
// change and Amplify rebuilds. Keep aligned with the backend `COGNITO_ENABLED`
// GitHub Environment variable (see `packages/infrastructure/environment.ts`).
export const configuration = {
  AIRWALLEX_ENV: 'demo',
  API_GATEWAY_URL:
    process.env.API_GATEWAY_URL ||
    'https://appbackend-dev-apigw.development.screenrights.app/',
  AUTH0_AUDIENCE: `https://*.${environmentName}.backend.screenrights.app`,
  AUTH0_CLIENTID: 'fuPwUH3qeDhR3GLTqAOeteSUQVZMfzgP',
  AUTH0_DOMAIN: 'dev-p3p4mf8f.us.auth0.com',
  AUTH0_DOMAIN_URL: '', // Populated dynamically below
  COGNITO_ENABLED: true,
  COGNITO_USER_POOL_ID: 'ap-southeast-2_sY7v3OXf8',
  COGNITO_APP_CLIENT_ID: '2h10utdg9et1b2p9gjmpbji0iv',
  COGNITO_APP_CLIENT_ID_MSR3_STAFF: 'a2l31s9nodtrul0c31cuk88qr',
  COGNITO_DOMAIN: 'auth-cognito.development.screenrights.app',
  COGNITO_REGION: 'ap-southeast-2',
  DOCUMENTS_S3_BUCKET: 'screenrights-documents-service-storage-dev',
  // One Google Tag Manager container is shared by every environment and by
  // both apps (APP-2232) -- there is deliberately no per-environment
  // override below and no measurement ID in this file. The container itself
  // holds a RegEx Table variable keyed on `{{Page Hostname}}` that routes
  // each hit to the right GA4 data stream, so the app does not need to know
  // which stream it is. An empty value means GTM does not load at all --
  // see utils/analytics/gtm.ts (initGtm no-ops on a falsy container ID). The
  // shared container is created under APP-2232.
  //
  // GTM_CONTAINER_ID below reads process.env first: that is a developer-
  // machine opt-out, not per-environment routing -- set it blank in
  // .env.local to switch GTM collection off locally. It is not, and should
  // not become, a way to point one environment at a different container.
  GTM_CONTAINER_ID: process.env.GTM_CONTAINER_ID || 'GTM-PVM9GRC5',
  GOOGLE_PLACES_API_KEY: 'AIzaSyCkLxd3vLaMpjrTs1cfbUzCNyT4jgi6bf8',
  // Override with MYSCREENRIGHTS_BASE in .env.local to point MSR2 embeds at an
  // MSR2 PR preview / sandbox, e.g. https://pr-123.devmy.screenrights.org or
  // https://sb-foo.devmy.screenrights.org. Honoured in all non-prod BUILD_ENVs
  // (staging/production hardcode their own value below). Drives the iframe src,
  // the postMessage origin check, and the local-dev CSP frame-src.
  MYSCREENRIGHTS_BASE:
    process.env.MYSCREENRIGHTS_BASE || 'https://devmy.screenrights.org',
  RETOOL_URL:
    'https://screenrights.retool.com/app/staff-app-main?_environment=development',
  SENTRY_DSN: process.env.SENTRY_DSN || '',
  // API base urls - populated dynamically below
  DOCUMENTS_API_BASE: '',
  PAYMENTS_API_BASE: '',
  PERMISSIONS_API_BASE: '',
  SERVICES_API_BASE: '',
  SHARED_API_BASE: '',
  TASKS_API_BASE: '',
  TITLES_API_BASE: '',
  WORKSPACES_API_BASE: '',
  // Audit & Reconciliation reports
  RECONCILIATION_REPORT_URL: `https://redash.screenrights.org/dashboard/${environmentName}---pre---reconciliation-reports`,
  AUDIT_REPORT_URL: `https://redash.screenrights.org/dashboard/${environmentName}---pre---audit-trail`,
  HIDE_CHATBOT: true,
};

// The dedicated preview Cognito pool (ADR-CM-028), for a PR preview opted in
// with the `/preview-pool on` PR command. That command sets
// USE_PREVIEW_COGNITO_POOL=true on the PR's own Amplify branch only, and Vite
// bakes it in. Keep these ids equal to PREVIEW_COGNITO_POOL in
// packages/backend/libs/common/src/services/auth/preview-cognito-pool.ts (a
// backend test compares them).
export const PREVIEW_COGNITO_POOL_CONFIGURATION = {
  COGNITO_USER_POOL_ID: 'ap-southeast-2_hE2Njt0zs',
  COGNITO_APP_CLIENT_ID: '4qq50dc8bojm1kuuv3vhmuqoe7',
  COGNITO_APP_CLIENT_ID_MSR3_STAFF: 'o7n2gec3u7d8phqtg369j6pip',
  COGNITO_DOMAIN: 'auth-cognito-preview.development.screenrights.app',
};

const usesPreviewCognitoPool = process.env.USE_PREVIEW_COGNITO_POOL === 'true';

// Overrides for specific BUILD_ENVs
switch (environmentName) {
  case 'local':
    Object.assign(configuration, {
      API_GATEWAY_URL: 'http://localhost:3000/proxy-apigw/',
      // Local still uses the development auth0 audience
      AUTH0_AUDIENCE: 'https://*.development.backend.screenrights.app',
      COGNITO_ENABLED: true,
      // MYSCREENRIGHTS_BASE inherits the env-overridable base above.
      // Audit & Reconciliation reports
      RECONCILIATION_REPORT_URL: `https://redash.screenrights.org`,
      AUDIT_REPORT_URL: `https://redash.screenrights.org`,
    });
    break;

  case 'preview':
    Object.assign(configuration, {
      API_GATEWAY_URL: process.env.API_GATEWAY_URL,
      // Preview still uses the development auth0 audience
      AUTH0_AUDIENCE: 'https://*.development.backend.screenrights.app',
      COGNITO_ENABLED: true,
      // Audit & Reconciliation reports
      RECONCILIATION_REPORT_URL: `https://redash.screenrights.org`,
      AUDIT_REPORT_URL: `https://redash.screenrights.org`,
      ...(usesPreviewCognitoPool ? PREVIEW_COGNITO_POOL_CONFIGURATION : {}),
    });
    break;

  case 'sandbox':
    Object.assign(configuration, {
      API_GATEWAY_URL: process.env.API_GATEWAY_URL,
      // Sandbox still uses the development auth0 audience
      AUTH0_AUDIENCE: 'https://*.development.backend.screenrights.app',
      COGNITO_ENABLED: true,
      // Audit & Reconciliation reports
      RECONCILIATION_REPORT_URL: `https://redash.screenrights.org`,
      AUDIT_REPORT_URL: `https://redash.screenrights.org`,
    });
    break;

  case 'staging':
    Object.assign(configuration, {
      API_GATEWAY_URL:
        'https://appbackend-stage-apigw.staging.screenrights.app/',
      AUTH0_CLIENTID: '2vFd6o9VgPGlVfYVwixIImHt3kSvNDoY',
      AUTH0_DOMAIN: 'auth.staging.screenrights.app',
      COGNITO_USER_POOL_ID: 'ap-southeast-2_tZnirKqoW',
      COGNITO_APP_CLIENT_ID: '48e6sd1q9ct8rmmbqa891iocgr',
      COGNITO_APP_CLIENT_ID_MSR3_STAFF: '6ch0p8n1kn6gcv5htvn1ofj5te',
      COGNITO_DOMAIN: 'auth-cognito.staging.screenrights.app',
      COGNITO_ENABLED: true,
      DOCUMENTS_S3_BUCKET: 'screenrights-documents-service-storage-staging',
      MYSCREENRIGHTS_BASE: 'https://testmy.screenrights.org',
      HIDE_CHATBOT: false,
      RETOOL_URL:
        'https://screenrights.retool.com/app/staff-app-main?_environment=staging',
      SENTRY_DSN:
        'https://fd399a8b709846b2b69e2665b670c56b@o166549.ingest.sentry.io/4505549229064192',
    });
    break;

  case 'production':
    Object.assign(configuration, {
      AIRWALLEX_ENV: 'prod',
      API_GATEWAY_URL: 'https://appbackend-prod-apigw.screenrights.app/',
      AUTH0_CLIENTID: 'nHv9WDWJH1PysREAcjpHptL0cCnlXj5l',
      AUTH0_DOMAIN: 'auth.screenrights.app',
      COGNITO_USER_POOL_ID: 'ap-southeast-2_sYhhwYLoK',
      COGNITO_APP_CLIENT_ID: '5bm6b1as761e68nbq6pst879id',
      COGNITO_APP_CLIENT_ID_MSR3_STAFF: '418k30p2v5cjei8njfjo3fvdbq',
      COGNITO_DOMAIN: 'auth-cognito.screenrights.app',
      COGNITO_ENABLED: true,
      DOCUMENTS_S3_BUCKET: 'screenrights-documents-service-storage',
      GOOGLE_PLACES_API_KEY: 'AIzaSyB1kT2rFdCraTlovE8dukfJucG0sUkXBN4',
      MYSCREENRIGHTS_BASE: 'https://my.screenrights.org',
      HIDE_CHATBOT: false,
      RETOOL_URL:
        'https://screenrights.retool.com/app/staff-app-main?_environment=production',
      SENTRY_DSN:
        'https://fd399a8b709846b2b69e2665b670c56b@o166549.ingest.sentry.io/4505549229064192',
    });
    break;
}

// Transition API base URLs to API Gateway (or Vite /proxy-apigw/ when BUILD_ENV=local).
// Admin Swagger uses these bases (e.g. WORKSPACES_API_BASE + '/docs-json'); they must match
// where your APIs run or OpenAPI will not reflect local backend changes.
const apiGatewayUrl = configuration.API_GATEWAY_URL;

if (apiGatewayUrl && apiGatewayUrl.includes('apigw')) {
  Object.assign(configuration, {
    // API base urls
    DOCUMENTS_API_BASE: `${apiGatewayUrl}documents`,
    PAYMENTS_API_BASE: `${apiGatewayUrl}payments`,
    PERMISSIONS_API_BASE: `${apiGatewayUrl}permissions`,
    SERVICES_API_BASE: `${apiGatewayUrl}services`,
    SHARED_API_BASE: `${apiGatewayUrl}shared`,
    TASKS_API_BASE: `${apiGatewayUrl}tasks`,
    TITLES_API_BASE: `${apiGatewayUrl}titles`,
    WORKSPACES_API_BASE: `${apiGatewayUrl}workspaces`,
  });
}

configuration['AUTH0_DOMAIN_URL'] = 'https://' + configuration.AUTH0_DOMAIN;

// Auth0 sign-in has been removed from the app (the Auth0 hosted login page is
// no longer available), so the old per-session `?auth=cognito|auth0` override
// is gone. Clear any stale override so a browser that previously forced Auth0
// reverts to the Cognito build default.
if (typeof window !== 'undefined') {
  localStorage.removeItem('sr-auth-provider-override');
}
